
import { useState } from 'react';
import { useDataRoom } from '../context/DataRoomContext';
import { DocumentType } from '../types/dataRoom';
import { downloadDocuments } from '../utils/documentUtils';

export const useDocumentActions = () => {
  const { 
    documents, 
    selectAllDocuments,
    getSelectedDocuments,
    updateDocumentTags,
    updateDocumentType
  } = useDataRoom();
  
  // Dialog state
  const [isEditTagsOpen, setIsEditTagsOpen] = useState(false);
  const [isEditTypeOpen, setIsEditTypeOpen] = useState(false);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [currentTags, setCurrentTags] = useState<string>('');
  const [currentType, setCurrentType] = useState<DocumentType>('other');
  
  // Document viewer
  const [isViewDocumentOpen, setIsViewDocumentOpen] = useState(false);
  const [viewDocumentUrl, setViewDocumentUrl] = useState<string>('');
  const [viewDocumentName, setViewDocumentName] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  
  const handleDownloadSelected = async () => {
    const selectedDocs = getSelectedDocuments();
    if (selectedDocs.length === 0 || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadDocuments(selectedDocs);
    } finally {
      setIsDownloading(false);
    }
  };
  
  const handleDownloadAll = async () => {
    if (documents.length === 0 || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadDocuments(documents);
    } finally {
      setIsDownloading(false);
    }
  };
  
  const handleSelectAll = (checked: boolean) => {
    selectAllDocuments(checked);
  };
  
  const openEditTags = (documentId: string, tags: string[]) => {
    setCurrentDocumentId(documentId);
    setCurrentTags(tags.join(', '));
    setIsEditTagsOpen(true);
  };
  
  const openEditType = (documentId: string, type: DocumentType) => {
    setCurrentDocumentId(documentId);
    setCurrentType(type);
    setIsEditTypeOpen(true);
  };
  
  const handleSaveTags = () => {
    const tags = currentTags.split(',').map(tag => tag.trim()).filter(tag => tag);
    updateDocumentTags(currentDocumentId, tags);
    setIsEditTagsOpen(false);
  };
  
  const handleSaveType = () => {
    updateDocumentType(currentDocumentId, currentType);
    setIsEditTypeOpen(false);
  };

  const handleViewDocument = async (document: string, _name: string) => {
    window.open(document, '_blank', 'noopener,noreferrer');
  };
  
  const closeViewDocument = () => {
    URL.revokeObjectURL(viewDocumentUrl);
    setIsViewDocumentOpen(false);
    setViewDocumentUrl('');
    setViewDocumentName('');
  };
  
  return {
    isEditTagsOpen,
    setIsEditTagsOpen,
    isEditTypeOpen,
    setIsEditTypeOpen,
    currentDocumentId,
    currentTags,
    setCurrentTags,
    currentType,
    setCurrentType,
    isViewDocumentOpen,
    viewDocumentUrl,
    viewDocumentName,
    isDownloading,
    handleDownloadSelected,
    handleDownloadAll,
    handleSelectAll,
    openEditTags,
    openEditType,
    handleSaveTags,
    handleSaveType,
    handleViewDocument,
    closeViewDocument,
  };
};
